<script setup lang="ts">
    import { usePriceHandler } from './composable/usePriceHandler';
    import { useEventStore } from '@/stores/event';
    import { useDisplay, useTheme } from 'vuetify';
    import { useEventCore } from '@/pages/core/event/composable/useEventCore';
    import { BetSelection, EventFeedType, EventPeriod, Jollywhere } from '@/constants';
    import { useBookmakerDictionary } from '@/stores';
    import { MarketHandicapItem } from '@/models/marketHandicap';
    import { formatOdds } from '@/helpers';
    import { oddsTooltip } from '@/helpers/event';
    import { usePlatform } from '@/composables/usePlatform';

    const {
        periodId = EventPeriod.FullTime,
        isShowAllMarketsToggle = false,
        isAllExpanded = false,
        isShow = false,
    } = defineProps<{
        periodId?: number;
        isShowAllMarketsToggle?: boolean;
        isAllExpanded?: boolean;
        isShow?: boolean;
    }>();

    const eventStore = useEventStore();
    const theme = useTheme();
    const display = useDisplay();
    const { onPriceClick } = usePriceHandler();
    const bookmakerDictionary = useBookmakerDictionary();
    const platform = usePlatform();

    const { isExpanded, isExpandedToggle, loadMore, loadMorePrices, getBestPrices } = useEventCore();

    const market = computed(() => {
        return periodId === EventPeriod.FirstHalf ? eventStore.marketHandicap1H! : eventStore.marketHandicapFT!;
    });

    const marketLines = computed(() => getBestPrices(market.value) as MarketHandicapItem[]);

    const { t } = useI18n();

    onMounted(() => {
        console.log('Component %s mounted.', 'app-eventMarketHDP');
    });

    const emit = defineEmits(['toggle-all-markets']);
    const toggleAllMarkets = () => emit('toggle-all-markets');

    const isShowComp = computed(() => isShow);

    defineExpose({
        isShow: isShowComp,
        name: 'app-eventMarketHDP',
        periodId: periodId,
        isExpanded,
    });
</script>

<template>
    <v-card
        tile
        elevation="0"
        style="padding-bottom: 1px"
        :style="eventStore.eventFeedType === EventFeedType.Live ? (theme.name.value === 'dark' ? 'background-color: #1e1f22' : 'background-color: #fff9f3') : ''"
    >
        <v-card-title class="font13 pa-0 market-header d-flex align-center" :class="eventStore.eventFeedType === EventFeedType.Live ? 'live' : 'pregame'">
            <v-avatar
                v-if="platform.isAggregator() && marketLines && marketLines[0]"
                :data-tooltip="{ text: `${marketLines[0].feedCount} ${t('event.bookmakers', marketLines[0].feedCount ?? 0)}`, location: 'top' }"
                size="22"
                class="ml-2"
                :color="theme.name.value === 'dark' ? '#2b2c30' : eventStore.eventFeedType === EventFeedType.Live ? '#bd6100' : '#c5c5c5'"
            >
                <span class="whiteText font12 bold">
                    {{ marketLines[0].feedCount }}
                </span>
            </v-avatar>
            <span :class="theme.name.value === 'dark' ? 'whiteText' : 'blackText'" class="pl-2 font-weight-regular">
                {{ t('event.asianHandicap') }}
            </span>
            <span v-if="periodId === EventPeriod.FirstHalf" :class="theme.name.value === 'dark' ? 'whiteText' : 'blackText'" class="pl-1 font-weight-regular">
                {{ t('event.market1H') }}
            </span>
            <span v-if="eventStore.stats !== null && eventStore.stats.score !== null" :class="theme.name.value === 'dark' ? 'whiteText' : 'blackText'" class="pl-1 font-weight-regular">
                ({{ eventStore.stats.score }})
            </span>
            <v-spacer></v-spacer>
            <v-btn tag="a" v-if="isShowAllMarketsToggle" width="105" size="small" variant="outlined" color="tbGreen" class="mr-2 toggle-button font12" @click="toggleAllMarkets">
                <span v-if="isAllExpanded">{{ t('event.hideAll') }}</span>
                <span v-else>{{ t('event.showAll') }}</span>
            </v-btn>
            <v-icon v-if="isExpanded" class="mr-2" @click="isExpandedToggle()" :icon="mdiChevronUp" />
            <v-icon v-else class="mr-2" @click="isExpandedToggle()" :icon="mdiChevronDown" />
        </v-card-title>
        <v-card-text v-if="isExpanded" class="pt-2">
            <v-row class="pa-0 my-0">
                <v-col class="text-center py-1"></v-col>
                <v-col cols="4" class="text-center py-1">
                    <span :class="theme.name.value === 'dark' ? 'white--text' : 'blackText'" class="font13 font-weight-medium">
                        {{ eventStore.teamHomeName }}
                    </span>
                </v-col>
                <v-col cols="1" class="text-center py-1">
                    <!--                                    <span class="font13 grey&#45;&#45;text">-->
                    <!--                                        {{ t('event.handicap') }}-->
                    <!--                                    </span>-->
                </v-col>
                <v-col cols="4" class="text-center py-1">
                    <span :class="theme.name.value === 'dark' ? 'white--text' : 'blackText'" class="font13 font-weight-medium">
                        {{ eventStore.teamAwayName }}
                    </span>
                </v-col>
                <v-col class="text-center py-1"></v-col>
            </v-row>

            <v-row v-for="(marketLine, i) in marketLines" :key="i" :class="display.width.value <= 430 ? 'px-0' : ''" class="py-1 my-0">
                <v-col :class="display.width.value <= 580 ? 'px-0' : ''" class="text-center py-1 d-flex flex-column align-center justify-center min-w">
                    <span v-if="platform.isAggregator()" class="bold grey--text" :class="display.width.value <= 430 ? 'font12' : 'font13'">
                        {{ marketLine.feedCount }}
                        {{ t('event.feeds', marketLine.feedCount) }}
                    </span>
                </v-col>
                <v-col :class="display.width.value <= 580 ? 'px-1' : ''" class="text-center py-1" cols="4">
                    <div
                        v-if="marketLine.homePrice"
                        class="mr-1 cursorPointer"
                        :class="[eventStore.eventFeedType === EventFeedType.Live ? 'live' : 'pregame', platform.isAggregator() ? 'odds-buttons' : 'odds-buttons-tbn']"
                        @click.stop="onPriceClick(marketLine, periodId, BetSelection.HandicapHome)"
                    >
                        <div class="font13" :class="platform.isBookmaker() ? 'price-title-tbn' : 'price-title-tb'" style="height: 18px">
                            <span>
                                {{ Number(marketLine.handicap) > 0 ? (marketLine.jollywhere === Jollywhere.Home ? '-' : '+') + marketLine.handicap : marketLine.handicap }}
                            </span>
                        </div>
                        <div v-xtooltip="platform.isAggregator() ? () => oddsTooltip(marketLine.homePriceList!, true, 0, 2) : null" :style="platform.isAggregator() ? 'height: 36px' : ''">
                            <div style="height: 18px">
                                <span :class="marketLine.getHighlightClass(BetSelection.HandicapHome, marketLine.homePrice)" class="odds nowrap font13">
                                    {{ formatOdds(marketLine.homePrice) }}
                                </span>
                            </div>

                            <span v-if="platform.isAggregator()" :class="['bookmaker' + marketLine.homeBookmakerId, 'font12']" style="height: 18px">
                                {{ bookmakerDictionary.nameById(marketLine.homeBookmakerId!) }}
                            </span>
                        </div>
                    </div>
                </v-col>
                <v-col :class="display.width.value <= 580 ? 'px-1' : ''" class="text-center py-1 d-flex flex-column align-center justify-center" cols="1">
                    <span class="grey--text" :class="display.width.value <= 430 ? 'font12' : 'font13'">
                        {{ marketLine.handicap }}
                    </span>
                </v-col>
                <v-col :class="display.width.value <= 580 ? 'px-1' : ''" class="text-center py-1" cols="4">
                    <div
                        v-if="marketLine.awayPrice"
                        class="mr-1 cursorPointer"
                        :class="[eventStore.eventFeedType === EventFeedType.Live ? 'live' : 'pregame', platform.isAggregator() ? 'odds-buttons' : 'odds-buttons-tbn']"
                        @click.stop="onPriceClick(marketLine, periodId, BetSelection.HandicapAway)"
                    >
                        <div class="font13" :class="platform.isBookmaker() ? 'price-title-tbn' : 'price-title-tb'" style="height: 18px">
                            <span>
                                {{ Number(marketLine.handicap) > 0 ? (marketLine.jollywhere === Jollywhere.Away ? '-' : '+') + marketLine.handicap : marketLine.handicap }}
                            </span>
                        </div>
                        <div v-xtooltip="platform.isAggregator() ? () => oddsTooltip(marketLine.awayPriceList!, true, 0, 2) : null" :style="platform.isAggregator() ? 'height: 36px' : ''">
                            <div style="height: 18px">
                                <span :class="marketLine.getHighlightClass(BetSelection.HandicapAway, marketLine.awayPrice)" class="odds nowrap font13">
                                    {{ formatOdds(marketLine.awayPrice) }}
                                </span>
                            </div>

                            <div v-if="platform.isAggregator()" :class="['bookmaker' + marketLine.awayBookmakerId, 'font12']" style="height: 18px">
                                {{ bookmakerDictionary.nameById(marketLine.awayBookmakerId!) }}
                            </div>
                        </div>
                    </div>
                </v-col>
                <v-col :class="display.width.value <= 580 ? 'px-0' : ''" class="text-center py-1 d-flex flex-column align-center justify-center min-w">
                    <span :class="[display.width.value <= 430 ? 'font12' : 'font13', Number(marketLine.marginPerc) > 100 ? 'greyText' : 'bold greenText']" v-if="marketLine.marginPerc">
                        {{ marketLine.marginPerc }}%
                    </span>
                </v-col>
            </v-row>
            <v-row v-if="loadMore !== true && market && market.length > 3">
                <v-btn tag="a" variant="outlined" small color="tbGreen" class="mx-auto showMore font12" style="border: 1px solid #e6e6e6; color: white" @click="loadMorePrices()">
                    {{ t('feed.showMore') }}
                </v-btn>
            </v-row>
            <v-row v-else-if="market && market.length > 3">
                <v-btn tag="a" variant="outlined" small color="tbGreen" class="mx-auto showMore font12" style="border: 1px solid #e6e6e6; color: white" @click="loadMore = false">
                    {{ t('feed.showLess') }}
                </v-btn>
            </v-row>
        </v-card-text>
    </v-card>
</template>

<style scoped>
    @import url('./styleMarketsCore.css');
</style>
